import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Radio,
  Chip,
  Divider
} from '@mui/material';
import {
  ChevronDown,
  ChevronRight,
  Layout,
  Boxes,
  Zap,
  Plug,
  Database,
  Server,
  Palette,
  Circle,
  Link2,
  X
} from 'lucide-react';

const sectionTypeIcons = {
  PAGES: <Layout size={18} />,
  COMPONENTS: <Boxes size={18} />,
  FEATURES: <Zap size={18} />,
  INTEGRATIONS: <Plug size={18} />,
  DATABASE: <Database size={18} />,
  INFRASTRUCTURE: <Server size={18} />,
  DESIGN: <Palette size={18} />
};

const sectionTypeLabels = {
  PAGES: 'Pages / Écrans',
  COMPONENTS: 'Composants UI',
  FEATURES: 'Fonctionnalités',
  INTEGRATIONS: 'Intégrations',
  DATABASE: 'Base de données',
  INFRASTRUCTURE: 'Infrastructure',
  DESIGN: 'Design / UX'
};

/**
 * DependencySelector - Dialog modal pour sélectionner un prérequis
 * 
 * @param {boolean} open - Si le dialog est ouvert
 * @param {function} onClose - Callback quand le dialog se ferme
 * @param {function} onSelect - Callback avec { type: 'section'|'item', id: string } ou null
 * @param {array} sections - Liste des sections avec leurs items
 * @param {string} sourceType - Type de l'élément source ('section' ou 'item')
 * @param {string} sourceId - ID de l'élément source (pour l'exclure de la sélection)
 * @param {string} sourceSectionId - ID de la section si source est un item (pour contexte)
 * @param {object} currentDependency - Dépendance actuelle { type, id } ou null
 */
function DependencySelector({ 
  open, 
  onClose, 
  onSelect, 
  sections = [], 
  sourceType,
  sourceId,
  sourceSectionId,
  currentDependency 
}) {
  const [expandedSections, setExpandedSections] = useState({});
  const [selected, setSelected] = useState(null);

  // Initialize selection from currentDependency
  React.useEffect(() => {
    if (open && currentDependency) {
      setSelected({ type: currentDependency.type, id: currentDependency.id });
    } else if (open) {
      setSelected(null);
    }
  }, [open, currentDependency]);

  // Expand all sections by default when opening
  React.useEffect(() => {
    if (open) {
      const expanded = {};
      sections.forEach(s => { expanded[s.id] = true; });
      setExpandedSections(expanded);
    }
  }, [open, sections]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleSelect = (type, id) => {
    if (selected?.type === type && selected?.id === id) {
      setSelected(null); // Deselect
    } else {
      setSelected({ type, id });
    }
  };

  const handleConfirm = () => {
    onSelect(selected);
    onClose();
  };

  const handleClear = () => {
    onSelect(null);
    onClose();
  };

  // Filter sections that can be selected as dependencies
  const selectableSections = useMemo(() => {
    return sections.filter(s => {
      // Can't depend on itself
      if (sourceType === 'section' && s.id === sourceId) return false;
      return true;
    });
  }, [sections, sourceType, sourceId]);

  // Get selected item name for display
  const getSelectedName = () => {
    if (!selected) return null;
    
    if (selected.type === 'section') {
      const section = sections.find(s => s.id === selected.id);
      return section ? `Section: ${section.name}` : null;
    } else {
      for (const section of sections) {
        const item = section.items?.find(i => i.id === selected.id);
        if (item) return `${section.name} > ${item.name}`;
      }
      return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ 
        sx: { 
          bgcolor: 'background.paper', 
          backgroundImage: 'none',
          maxHeight: '80vh'
        } 
      }}
    >
      <DialogTitle sx={{ 
        fontWeight: 'bold', 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <Link2 size={20} color="#f59e0b" />
        Définir un prérequis
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, bgcolor: 'rgba(245, 158, 11, 0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <Typography variant="body2" color="text.secondary">
            {sourceType === 'section' 
              ? 'Sélectionnez une section qui doit être terminée avant celle-ci.'
              : 'Sélectionnez une section ou un élément qui doit être terminé avant celui-ci.'
            }
          </Typography>
          {selected && (
            <Chip 
              label={getSelectedName()}
              onDelete={() => setSelected(null)}
              sx={{ mt: 1, bgcolor: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b' }}
              size="small"
            />
          )}
        </Box>

        <List sx={{ py: 0, maxHeight: 400, overflowY: 'auto' }}>
          {/* Option "Aucun prérequis" */}
          <ListItemButton 
            onClick={handleClear}
            sx={{ 
              py: 1.5,
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              bgcolor: !selected ? 'rgba(255,255,255,0.02)' : 'transparent'
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <X size={18} color="#888" />
            </ListItemIcon>
            <ListItemText 
              primary="Aucun prérequis" 
              primaryTypographyProps={{ color: 'text.secondary' }}
            />
          </ListItemButton>

          <Divider />

          {/* Sections et items */}
          {selectableSections.map((section) => {
            const isExpanded = expandedSections[section.id];
            const isSectionSelected = selected?.type === 'section' && selected?.id === section.id;
            
            // Filter items - can't depend on itself
            const selectableItems = section.items?.filter(item => {
              if (sourceType === 'item' && item.id === sourceId) return false;
              return true;
            }) || [];

            return (
              <Box key={section.id}>
                {/* Section header */}
                <ListItemButton
                  sx={{ 
                    py: 1,
                    bgcolor: isSectionSelected ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
                    '&:hover': { bgcolor: isSectionSelected ? 'rgba(245, 158, 11, 0.15)' : 'rgba(255,255,255,0.02)' }
                  }}
                >
                  {/* Expand/collapse for items */}
                  <ListItemIcon 
                    sx={{ minWidth: 32, cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); toggleSection(section.id); }}
                  >
                    {selectableItems.length > 0 ? (
                      isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                    ) : <Box sx={{ width: 16 }} />}
                  </ListItemIcon>

                  {/* Radio for section selection */}
                  <Radio
                    checked={isSectionSelected}
                    onChange={() => handleSelect('section', section.id)}
                    size="small"
                    sx={{ 
                      p: 0.5, 
                      mr: 1,
                      color: 'rgba(255,255,255,0.3)',
                      '&.Mui-checked': { color: '#f59e0b' }
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />

                  {/* Section icon */}
                  <ListItemIcon sx={{ minWidth: 32, color: '#00d4aa' }}>
                    {sectionTypeIcons[section.type]}
                  </ListItemIcon>

                  {/* Section name */}
                  <ListItemText 
                    primary={section.name}
                    secondary={sectionTypeLabels[section.type]}
                    primaryTypographyProps={{ fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                    onClick={() => handleSelect('section', section.id)}
                  />

                  {/* Item count */}
                  {selectableItems.length > 0 && (
                    <Chip 
                      label={`${selectableItems.length} élément${selectableItems.length > 1 ? 's' : ''}`}
                      size="small"
                      sx={{ 
                        height: 20, 
                        fontSize: '0.65rem',
                        bgcolor: 'rgba(255,255,255,0.05)'
                      }}
                    />
                  )}
                </ListItemButton>

                {/* Items (only for item source - items can depend on other items) */}
                {sourceType === 'item' && selectableItems.length > 0 && (
                  <Collapse in={isExpanded}>
                    <List sx={{ py: 0 }}>
                      {selectableItems.map((item) => {
                        const isItemSelected = selected?.type === 'item' && selected?.id === item.id;
                        
                        return (
                          <ListItemButton
                            key={item.id}
                            sx={{ 
                              pl: 8,
                              py: 0.75,
                              bgcolor: isItemSelected ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
                              '&:hover': { bgcolor: isItemSelected ? 'rgba(245, 158, 11, 0.15)' : 'rgba(255,255,255,0.02)' }
                            }}
                            onClick={() => handleSelect('item', item.id)}
                          >
                            <Radio
                              checked={isItemSelected}
                              size="small"
                              sx={{ 
                                p: 0.5, 
                                mr: 1,
                                color: 'rgba(255,255,255,0.3)',
                                '&.Mui-checked': { color: '#f59e0b' }
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <Circle size={8} color={isItemSelected ? '#f59e0b' : '#666'} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={item.name}
                              primaryTypographyProps={{ 
                                variant: 'body2',
                                color: isItemSelected ? '#f59e0b' : 'text.primary'
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </Box>
            );
          })}

          {selectableSections.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              Aucun élément disponible comme prérequis.
            </Typography>
          )}
        </List>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Button onClick={onClose} color="inherit">
          Annuler
        </Button>
        <Button 
          onClick={handleConfirm}
          variant="contained"
          sx={{ 
            bgcolor: '#f59e0b', 
            '&:hover': { bgcolor: '#d97706' }
          }}
        >
          {selected ? 'Confirmer' : 'Retirer le prérequis'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DependencySelector;
